import { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Save, Plus, Trash2, Upload, ImageIcon } from "lucide-react";
import { toast } from "sonner";

export default function SuperAdminContent() {
  const [data, setData] = useState(null);
  const heroInput = useRef(null);
  const [heroBusy, setHeroBusy] = useState(false);

  const load = async () => setData((await api.get("/site-content")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put("/super-admin/site-content", data);
      toast.success("Content saved");
      await load();
    } catch (e) { toast.error("Failed to save"); }
  };

  const uploadHero = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setHeroBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      await api.post("/super-admin/site-content/hero-image", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Hero image uploaded");
      await load();
    } catch { toast.error("Upload failed"); }
    finally { setHeroBusy(false); if (heroInput.current) heroInput.current.value = ""; }
  };

  const removeHero = async () => {
    try {
      await api.delete("/super-admin/site-content/hero-image");
      toast.success("Hero image removed");
      await load();
    } catch { toast.error("Could not remove"); }
  };

  if (!data) return <AdminShell title="Site content" scope="super"><div /></AdminShell>;

  const set = (path, value) => {
    const copy = JSON.parse(JSON.stringify(data));
    const keys = path.split(".");
    let ref = copy;
    for (let i = 0; i < keys.length - 1; i++) ref = ref[keys[i]];
    ref[keys[keys.length - 1]] = value;
    setData(copy);
  };

  const updateFeature = (i, field, value) => {
    const copy = JSON.parse(JSON.stringify(data));
    copy.landing.features[i][field] = value;
    setData(copy);
  };
  const addFeature = () => {
    const copy = JSON.parse(JSON.stringify(data));
    copy.landing.features.push({ title: "New feature", body: "Description…" });
    setData(copy);
  };
  const removeFeature = (i) => {
    const copy = JSON.parse(JSON.stringify(data));
    copy.landing.features.splice(i, 1);
    setData(copy);
  };

  const updateValue = (i, field, value) => {
    const copy = JSON.parse(JSON.stringify(data));
    copy.about.values[i][field] = value;
    setData(copy);
  };
  const addValue = () => {
    const copy = JSON.parse(JSON.stringify(data));
    copy.about.values.push({ title: "New value", body: "Description…" });
    setData(copy);
  };
  const removeValue = (i) => {
    const copy = JSON.parse(JSON.stringify(data));
    copy.about.values.splice(i, 1);
    setData(copy);
  };

  return (
    <AdminShell title="Site content" scope="super">
      <div className="flex justify-between items-center mb-6">
        <p className="text-mr-secondary max-w-2xl">Edit the marketing pages of MedRewards.app. Changes are live immediately.</p>
        <Button data-testid="content-save-btn" onClick={save} className="rounded-full bg-mr-primary hover:bg-[var(--mr-primary-hover)]"><Save className="h-4 w-4 mr-2" /> Save all</Button>
      </div>

      <Tabs defaultValue="landing">
        <TabsList className="bg-white border border-mr rounded-full p-1 flex-wrap h-auto">
          <TabsTrigger data-testid="content-tab-landing" value="landing" className="rounded-full data-[state=active]:bg-mr-primary data-[state=active]:text-white">Landing</TabsTrigger>
          <TabsTrigger data-testid="content-tab-about" value="about" className="rounded-full data-[state=active]:bg-mr-primary data-[state=active]:text-white">About</TabsTrigger>
          <TabsTrigger data-testid="content-tab-contact" value="contact" className="rounded-full data-[state=active]:bg-mr-primary data-[state=active]:text-white">Contact</TabsTrigger>
          <TabsTrigger data-testid="content-tab-footer" value="footer" className="rounded-full data-[state=active]:bg-mr-primary data-[state=active]:text-white">Footer</TabsTrigger>
          <TabsTrigger data-testid="content-tab-privacy" value="privacy" className="rounded-full data-[state=active]:bg-mr-primary data-[state=active]:text-white">Privacy</TabsTrigger>
          <TabsTrigger data-testid="content-tab-terms" value="terms" className="rounded-full data-[state=active]:bg-mr-primary data-[state=active]:text-white">Terms</TabsTrigger>
        </TabsList>

        <TabsContent value="landing" className="mt-6 space-y-4">
          <div className="bg-white border border-mr rounded-2xl p-6 space-y-4">
            <h2 className="font-display text-lg font-semibold text-mr-primary">Hero image</h2>
            <p className="text-mr-secondary text-sm">Replace the default photo on the homepage. The image is <b>hidden on mobile</b> automatically. Recommended ratio 4:5 (portrait), at least 1200 × 1500 px.</p>

            <div>
              <Label>Image link (URL)</Label>
              <Input
                data-testid="content-hero-image-url"
                value={data.landing.hero_image_url || ""}
                onChange={(e) => set("landing.hero_image_url", e.target.value)}
                placeholder="https://images.example.com/photo.jpg"
                className="mt-2"
              />
              <p className="text-xs text-mr-secondary mt-1">Paste a link to any public image. Takes priority over uploaded file. Leave empty to use uploaded image instead.</p>
            </div>

            <div className="border-t border-mr-border pt-4">
              <Label>Or upload from your device</Label>
              <div className="mt-2 flex items-start gap-4">
                <div className="w-40 aspect-[4/5] rounded-xl overflow-hidden border border-mr bg-mr-bg flex items-center justify-center">
                  {data.landing.hero_image_path ? (
                    <img src={fileUrl(data.landing.hero_image_path)} alt="hero" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-mr-secondary" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input data-testid="content-hero-image-input" ref={heroInput} type="file" accept="image/*" className="hidden" onChange={uploadHero} />
                  <Button data-testid="content-hero-image-upload" disabled={heroBusy} variant="outline" className="rounded-full" onClick={() => heroInput.current?.click()}>
                    <Upload className="h-4 w-4 mr-2" /> {heroBusy ? "Uploading…" : (data.landing.hero_image_path ? "Replace image" : "Upload image")}
                  </Button>
                  {data.landing.hero_image_path && (
                    <Button data-testid="content-hero-image-remove" variant="ghost" className="rounded-full text-mr-accent" onClick={removeHero}>
                      <Trash2 className="h-4 w-4 mr-2" /> Remove uploaded
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-mr rounded-2xl p-6 space-y-4">
            <h2 className="font-display text-lg font-semibold text-mr-primary">Hero text</h2>
            <div><Label>Eyebrow text</Label><Input data-testid="content-hero-eyebrow" value={data.landing.hero_eyebrow} onChange={(e) => set("landing.hero_eyebrow", e.target.value)} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Title part 1</Label><Input data-testid="content-hero-p1" value={data.landing.hero_title_part1} onChange={(e) => set("landing.hero_title_part1", e.target.value)} /></div>
              <div><Label>Accent word</Label><Input data-testid="content-hero-accent" value={data.landing.hero_title_accent} onChange={(e) => set("landing.hero_title_accent", e.target.value)} /></div>
              <div><Label>Title part 2</Label><Input data-testid="content-hero-p2" value={data.landing.hero_title_part2} onChange={(e) => set("landing.hero_title_part2", e.target.value)} /></div>
            </div>
            <div><Label>Sub-headline</Label><Textarea data-testid="content-hero-sub" rows={3} value={data.landing.hero_subtitle} onChange={(e) => set("landing.hero_subtitle", e.target.value)} /></div>
          </div>

          <div className="bg-white border border-mr rounded-2xl p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-lg font-semibold text-mr-primary">Feature blocks</h2>
              <Button data-testid="content-feature-add" variant="outline" size="sm" className="rounded-full" onClick={addFeature}><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>
            {data.landing.features.map((f, i) => (
              <div key={i} className="grid sm:grid-cols-[1fr_2fr_auto] gap-3 items-end border-t border-mr-border first:border-0 pt-4 first:pt-0 mt-4 first:mt-0">
                <div><Label className="text-xs">Title</Label><Input data-testid={`content-feature-title-${i}`} value={f.title} onChange={(e) => updateFeature(i, "title", e.target.value)} /></div>
                <div><Label className="text-xs">Body</Label><Textarea data-testid={`content-feature-body-${i}`} rows={2} value={f.body} onChange={(e) => updateFeature(i, "body", e.target.value)} /></div>
                <Button data-testid={`content-feature-del-${i}`} variant="ghost" size="icon" onClick={() => removeFeature(i)}><Trash2 className="h-4 w-4 text-mr-accent" /></Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="about" className="mt-6 space-y-4">
          <div className="bg-white border border-mr rounded-2xl p-6 space-y-4">
            <div><Label>Title</Label><Input data-testid="content-about-title" value={data.about.title} onChange={(e) => set("about.title", e.target.value)} /></div>
            <div><Label>Intro</Label><Textarea data-testid="content-about-intro" rows={3} value={data.about.intro} onChange={(e) => set("about.intro", e.target.value)} /></div>
            <div><Label>Body (supports new lines)</Label><Textarea data-testid="content-about-body" rows={8} value={data.about.body} onChange={(e) => set("about.body", e.target.value)} /></div>
          </div>
          <div className="bg-white border border-mr rounded-2xl p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-lg font-semibold text-mr-primary">Values</h2>
              <Button data-testid="content-value-add" variant="outline" size="sm" className="rounded-full" onClick={addValue}><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>
            {(data.about.values || []).map((v, i) => (
              <div key={i} className="grid sm:grid-cols-[1fr_2fr_auto] gap-3 items-end border-t border-mr-border first:border-0 pt-4 first:pt-0 mt-4 first:mt-0">
                <div><Label className="text-xs">Title</Label><Input data-testid={`content-value-title-${i}`} value={v.title} onChange={(e) => updateValue(i, "title", e.target.value)} /></div>
                <div><Label className="text-xs">Body</Label><Textarea data-testid={`content-value-body-${i}`} rows={2} value={v.body} onChange={(e) => updateValue(i, "body", e.target.value)} /></div>
                <Button data-testid={`content-value-del-${i}`} variant="ghost" size="icon" onClick={() => removeValue(i)}><Trash2 className="h-4 w-4 text-mr-accent" /></Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="contact" className="mt-6">
          <div className="bg-white border border-mr rounded-2xl p-6 space-y-4">
            <div><Label>Intro</Label><Textarea data-testid="content-contact-intro" rows={3} value={data.contact.intro} onChange={(e) => set("contact.intro", e.target.value)} /></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Email</Label><Input data-testid="content-contact-email" type="email" value={data.contact.email} onChange={(e) => set("contact.email", e.target.value)} /></div>
              <div><Label>Phone</Label><Input data-testid="content-contact-phone" value={data.contact.phone} onChange={(e) => set("contact.phone", e.target.value)} /></div>
            </div>
            <div><Label>Address</Label><Input data-testid="content-contact-address" value={data.contact.address} onChange={(e) => set("contact.address", e.target.value)} /></div>
            <div><Label>Hours</Label><Input data-testid="content-contact-hours" value={data.contact.hours} onChange={(e) => set("contact.hours", e.target.value)} /></div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label>LinkedIn URL</Label><Input data-testid="content-contact-linkedin" value={data.contact.linkedin || ""} onChange={(e) => set("contact.linkedin", e.target.value)} /></div>
              <div><Label>Twitter URL</Label><Input data-testid="content-contact-twitter" value={data.contact.twitter || ""} onChange={(e) => set("contact.twitter", e.target.value)} /></div>
              <div><Label>Instagram URL</Label><Input data-testid="content-contact-instagram" value={data.contact.instagram || ""} onChange={(e) => set("contact.instagram", e.target.value)} /></div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="footer" className="mt-6">
          <div className="bg-white border border-mr rounded-2xl p-6">
            <div><Label>Footer tagline</Label><Input data-testid="content-footer-tagline" value={data.footer.tagline} onChange={(e) => set("footer.tagline", e.target.value)} /></div>
          </div>
        </TabsContent>

        <TabsContent value="privacy" className="mt-6">
          <div className="bg-white border border-mr rounded-2xl p-6 space-y-4">
            <div><Label>Title</Label><Input data-testid="content-privacy-title" value={data.privacy.title} onChange={(e) => set("privacy.title", e.target.value)} /></div>
            <div><Label>Updated at</Label><Input data-testid="content-privacy-updated" value={data.privacy.updated_at} onChange={(e) => set("privacy.updated_at", e.target.value)} /></div>
            <div><Label>Body</Label><Textarea data-testid="content-privacy-body" rows={20} className="font-mono text-xs" value={data.privacy.body} onChange={(e) => set("privacy.body", e.target.value)} /></div>
          </div>
        </TabsContent>

        <TabsContent value="terms" className="mt-6">
          <div className="bg-white border border-mr rounded-2xl p-6 space-y-4">
            <div><Label>Title</Label><Input data-testid="content-terms-title" value={data.terms.title} onChange={(e) => set("terms.title", e.target.value)} /></div>
            <div><Label>Updated at</Label><Input data-testid="content-terms-updated" value={data.terms.updated_at} onChange={(e) => set("terms.updated_at", e.target.value)} /></div>
            <div><Label>Body</Label><Textarea data-testid="content-terms-body" rows={20} className="font-mono text-xs" value={data.terms.body} onChange={(e) => set("terms.body", e.target.value)} /></div>
          </div>
        </TabsContent>
      </Tabs>
    </AdminShell>
  );
}
